import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, LogOut, ArrowLeft, Shield, Loader2, 
  Newspaper, Edit, Eye, EyeOff, X, Save, RefreshCw, Upload, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionLabels = {
  transporters: 'privateTransporters',
  purchases: 'privatePurchases',
  ecommerce: 'privateEcommerce',
  clients: 'privateClients',
};

const userTypes = ['transporters', 'purchases', 'ecommerce', 'clients'];
const newsCategories = ['sustainability', 'corporate', 'quality', 'product'];

const emptyArticle = {
  title: { en: '', es: '', fr: '', de: '', nl: '' },
  summary: { en: '', es: '', fr: '', de: '', nl: '' },
  content: { en: '', es: '', fr: '', de: '', nl: '' },
  category: 'corporate',
  image_url: '',
  images: [],
  published_at: new Date().toISOString().split('T')[0],
  author: 'TRH Steel Communications',
  is_published: true
};

export const PrivatePortal = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || 'clients';
  
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    user_type: section,
    is_admin: false,
  });
  const [creating, setCreating] = useState(false);

  // News Management State
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'news'
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [newsForm, setNewsForm] = useState(emptyArticle);
  const [savingNews, setSavingNews] = useState(false);
  const [editLang, setEditLang] = useState('es');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    const storedUser = sessionStorage.getItem('privateUser');
    if (!storedUser) {
      navigate(`/private/login?section=${section}`);
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, navigate]);

  useEffect(() => {
    if (activeTab === 'news' && user?.is_admin) {
      fetchNews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/private/users`, {
        params: { user_type: section }
      });
      setUsers(response.data.users);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/news`);
      setNewsArticles(response.data.articles || []);
    } catch (error) {
      toast.error('Error loading news articles');
    } finally {
      setNewsLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API}/private/users`, newUser);
      toast.success(t('userCreated'));
      setNewUser({
        username: '',
        password: '',
        user_type: section,
        is_admin: false,
      });
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('confirmDelete'))) return;

    try {
      await axios.delete(`${API}/private/users/${userId}`);
      toast.success(t('userDeleted'));
      fetchUsers();
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('privateUser');
    navigate('/');
  };

  // News Management Functions
  const openCreateNews = () => {
    setEditingArticle(null);
    setNewsForm(emptyArticle);
    setShowNewsForm(true);
  };

  const openEditNews = (article) => {
    setEditingArticle(article);
    setNewsForm({
      title: article.title || { en: '', es: '', fr: '', de: '', nl: '' },
      summary: article.summary || { en: '', es: '', fr: '', de: '', nl: '' },
      content: article.content || { en: '', es: '', fr: '', de: '', nl: '' },
      category: article.category || 'corporate',
      image_url: article.image_url || '',
      images: article.images || [],
      published_at: article.published_at || new Date().toISOString().split('T')[0],
      author: article.author || 'TRH Steel Communications',
      is_published: article.is_published !== false
    });
    setShowNewsForm(true);
  };

  const closeNewsForm = () => {
    setShowNewsForm(false);
    setEditingArticle(null);
    setNewsForm(emptyArticle);
  };

  const handleSaveNews = async () => {
    setSavingNews(true);
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/news/${editingArticle.id}`, newsForm);
        toast.success('Noticia actualizada');
      } else {
        await axios.post(`${API}/admin/news`, newsForm);
        toast.success('Noticia creada');
      }
      closeNewsForm();
      fetchNews();
    } catch (error) {
      toast.error('Error al guardar la noticia');
    } finally {
      setSavingNews(false);
    }
  };

  const handleDeleteNews = async (articleId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta noticia?')) return;

    try {
      await axios.delete(`${API}/admin/news/${articleId}`);
      toast.success('Noticia eliminada');
      fetchNews();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleImportDefaults = async () => {
    try {
      const response = await axios.post(`${API}/admin/news/import-defaults`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchNews();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Error importing default articles');
    }
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name}: El archivo es demasiado grande (máx 5MB)`);
          continue;
        }
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name}: Solo se permiten imágenes`);
          continue;
        }

        // Convert to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload to server
        const formData = new URLSearchParams();
        formData.append('file_name', file.name);
        formData.append('file_data', base64);
        formData.append('file_type', file.type);

        const response = await axios.post(`${API}/admin/news/upload-image`, formData, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });

        if (response.data.success) {
          uploadedUrls.push(`${BACKEND_URL}${response.data.image_url}`);
        }
      }

      if (uploadedUrls.length > 0) {
        setNewsForm(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedUrls],
          // Set the first image as the main image_url if not already set
          image_url: prev.image_url || uploadedUrls[0]
        }));
        toast.success(`${uploadedUrls.length} imagen(es) subida(s) correctamente`);
      }
    } catch (error) {
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
      // Reset the input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const removeImage = (indexToRemove) => {
    setNewsForm(prev => {
      const newImages = prev.images.filter((_, idx) => idx !== indexToRemove);
      return {
        ...prev,
        images: newImages,
        // If removing the main image, set the next one
        image_url: newImages.length > 0 ? newImages[0] : ''
      };
    });
  };

  const setMainImage = (url) => {
    setNewsForm(prev => ({ ...prev, image_url: url }));
  };

  const updateNewsFormField = (field, lang, value) => {
    setNewsForm(prev => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value }
    }));
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="private-portal-page">
      {/* Header */}
      <header className="bg-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="font-heading text-xl font-bold">
                TRH<span className="text-red-300">STEEL</span>
              </Link>
              <span className="text-red-200">|</span>
              <span className="font-bold uppercase tracking-wider">
                {t('privateArea')} - {t(sectionLabels[section])}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span>{user.username}</span>
                {user.is_admin && (
                  <span className="bg-red-900 text-xs px-2 py-0.5 rounded">ADMIN</span>
                )}
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-white hover:bg-red-800"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-6 rounded-none"
          data-testid="back-to-home"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('backToPortal')}
        </Button>

        {/* Admin Tabs */}
        {user.is_admin && (
          <div className="flex space-x-2 mb-6">
            <Button
              variant={activeTab === 'users' ? 'default' : 'outline'}
              onClick={() => setActiveTab('users')}
              className={`rounded-none uppercase tracking-wider text-xs font-bold ${
                activeTab === 'users' ? 'bg-red-700 hover:bg-red-800' : ''
              }`}
              data-testid="tab-users"
            >
              <Users className="h-4 w-4 mr-2" />
              {t('userManagement')}
            </Button>
            <Button
              variant={activeTab === 'news' ? 'default' : 'outline'}
              onClick={() => setActiveTab('news')}
              className={`rounded-none uppercase tracking-wider text-xs font-bold ${
                activeTab === 'news' ? 'bg-red-700 hover:bg-red-800' : ''
              }`}
              data-testid="tab-news"
            >
              <Newspaper className="h-4 w-4 mr-2" />
              {t('newsManagement')}
            </Button>
          </div>
        )}

        {/* User Management Section (Admin Only) */}
        {user.is_admin && activeTab === 'users' && (
          <div className="bg-white border border-slate-200 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-6 w-6 text-slate-600" />
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                  {t('userManagement')}
                </h2>
              </div>
              <Button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="bg-red-700 hover:bg-red-800 text-white rounded-none uppercase tracking-wider text-xs font-bold"
                data-testid="toggle-create-form"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('createUser')}
              </Button>
            </div>

            {/* Create User Form */}
            {showCreateForm && (
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                <form onSubmit={handleCreateUser} className="grid md:grid-cols-4 gap-4" data-testid="create-user-form">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('username')}</Label>
                    <Input
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                      required
                      className="rounded-none"
                      data-testid="new-user-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('password')}</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                      className="rounded-none"
                      data-testid="new-user-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('userType')}</Label>
                    <Select
                      value={newUser.user_type}
                      onValueChange={(value) => setNewUser({ ...newUser, user_type: value })}
                    >
                      <SelectTrigger className="rounded-none" data-testid="new-user-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {userTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(sectionLabels[type])}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      disabled={creating}
                      className="w-full bg-green-600 hover:bg-green-700 text-white rounded-none"
                      data-testid="submit-create-user"
                    >
                      {creating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('createUser')
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Users Table */}
            <div className="p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">
                {t('userList')} - {t(sectionLabels[section])}
              </h3>
              
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  {t('noUsers')}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-heading uppercase tracking-wider">{t('username')}</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">{t('userType')}</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">Admin</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.username}</TableCell>
                        <TableCell>{t(sectionLabels[u.user_type]) || u.user_type}</TableCell>
                        <TableCell>
                          {u.is_admin ? (
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1">
                              ADMIN
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}

        {/* News Management Section (Admin Only) */}
        {user.is_admin && activeTab === 'news' && (
          <div className="bg-white border border-slate-200 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Newspaper className="h-6 w-6 text-slate-600" />
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                  {t('newsManagement')}
                </h2>
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={handleImportDefaults}
                  variant="outline"
                  className="rounded-none uppercase tracking-wider text-xs font-bold"
                  data-testid="import-defaults-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('importDefaults')}
                </Button>
                <Button
                  onClick={openCreateNews}
                  className="bg-red-700 hover:bg-red-800 text-white rounded-none uppercase tracking-wider text-xs font-bold"
                  data-testid="create-news-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('newArticle')}
                </Button>
              </div>
            </div>

            {/* News Table */}
            <div className="p-6">
              {newsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : newsArticles.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Newspaper className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>{t('noNewsInDb')}</p>
                  <p className="text-sm mt-2">{t('clickImportDefaults')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-heading uppercase tracking-wider">{t('titleColumn')}</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">{t('categoryColumn')}</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">{t('dateColumn')}</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">{t('statusColumn')}</TableHead>
                      <TableHead className="font-heading uppercase tracking-wider">{t('actionsColumn')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newsArticles.map((article) => (
                      <TableRow key={article.id} data-testid={`news-row-${article.id}`}>
                        <TableCell className="font-medium max-w-xs truncate">
                          {article.title?.es || article.title?.en || t('noTitle')}
                        </TableCell>
                        <TableCell>
                          <span className="bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1 capitalize">
                            {article.category}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {article.published_at}
                        </TableCell>
                        <TableCell>
                          {article.is_published !== false ? (
                            <span className="flex items-center text-green-600 text-xs">
                              <Eye className="h-3 w-3 mr-1" /> {t('published')}
                            </span>
                          ) : (
                            <span className="flex items-center text-slate-400 text-xs">
                              <EyeOff className="h-3 w-3 mr-1" /> {t('draft')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditNews(article)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-news-${article.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNews(article.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-news-${article.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}

        {/* Non-Admin Welcome */}
        {!user.is_admin && (
          <div className="bg-white border border-slate-200 shadow-sm p-8 text-center">
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-4">
              {t('welcome')}, {user.username}!
            </h2>
            <p className="text-slate-600">
              {t(sectionLabels[section])} Portal
            </p>
          </div>
        )}
      </main>

      {/* News Edit/Create Modal */}
      {showNewsForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeNewsForm}>
          <div 
            className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-slate-200 flex items-center justify-between z-10">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                {editingArticle ? t('editArticle') : t('newArticle')}
              </h2>
              <Button variant="ghost" size="sm" onClick={closeNewsForm}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Language Tabs */}
              <div className="flex space-x-2 border-b border-slate-200 pb-2">
                {['es', 'en', 'fr', 'de', 'nl'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setEditLang(lang)}
                    className={`px-3 py-1 text-sm font-medium uppercase ${
                      editLang === lang 
                        ? 'text-red-700 border-b-2 border-red-700' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('articleTitle')} ({editLang.toUpperCase()})</Label>
                <Input
                  value={newsForm.title[editLang] || ''}
                  onChange={(e) => updateNewsFormField('title', editLang, e.target.value)}
                  placeholder={t('articleTitlePlaceholder')}
                  className="rounded-none"
                />
              </div>

              {/* Summary */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('articleSummary')} ({editLang.toUpperCase()})</Label>
                <textarea
                  value={newsForm.summary[editLang] || ''}
                  onChange={(e) => updateNewsFormField('summary', editLang, e.target.value)}
                  placeholder={t('articleSummaryPlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none resize-none"
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('articleContent')} ({editLang.toUpperCase()})</Label>
                <textarea
                  value={newsForm.content[editLang] || ''}
                  onChange={(e) => updateNewsFormField('content', editLang, e.target.value)}
                  placeholder={t('articleContentPlaceholder')}
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none resize-none"
                />
              </div>

              {/* Other fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('categoryColumn')}</Label>
                  <Select
                    value={newsForm.category}
                    onValueChange={(value) => setNewsForm({ ...newsForm, category: value })}
                  >
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {newsCategories.map((cat) => (
                        <SelectItem key={cat} value={cat} className="capitalize">
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('publicationDate')}</Label>
                  <Input
                    type="date"
                    value={newsForm.published_at}
                    onChange={(e) => setNewsForm({ ...newsForm, published_at: e.target.value })}
                    className="rounded-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('author')}</Label>
                  <Input
                    value={newsForm.author}
                    onChange={(e) => setNewsForm({ ...newsForm, author: e.target.value })}
                    placeholder={t('authorPlaceholder')}
                    className="rounded-none"
                  />
                </div>
              </div>

              {/* Image Upload Area */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">{t('newsImages')}</Label>
                
                {/* Upload Button */}
                <div
                  onClick={() => imageInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 p-6 text-center cursor-pointer hover:border-red-500 hover:bg-red-50/30 transition-colors"
                >
                  {uploadingImage ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 text-red-600 animate-spin mb-2" />
                      <p className="text-sm text-slate-600">{t('uploadingImage')}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-slate-400 mb-2" />
                      <p className="text-sm font-medium text-slate-700">{t('clickToUploadImages')}</p>
                      <p className="text-xs text-slate-500 mt-1">{t('imageFormats')}</p>
                    </div>
                  )}
                </div>
                
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />

                {/* Uploaded Images Grid */}
                {newsForm.images && newsForm.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {newsForm.images.map((imgUrl, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={imgUrl}
                          alt={`${t('newsImages')} ${idx + 1}`}
                          className={`w-full h-24 object-cover border-2 ${
                            newsForm.image_url === imgUrl ? 'border-green-500' : 'border-slate-200'
                          }`}
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                        />
                        {/* Overlay with actions */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                          <button
                            type="button"
                            onClick={() => setMainImage(imgUrl)}
                            className={`p-1.5 rounded ${
                              newsForm.image_url === imgUrl 
                                ? 'bg-green-500 text-white' 
                                : 'bg-white text-slate-700 hover:bg-green-100'
                            }`}
                            title={t('setAsMainImage')}
                          >
                            <ImageIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="p-1.5 bg-white text-red-600 rounded hover:bg-red-100"
                            title={t('deleteImage')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {/* Main image badge */}
                        {newsForm.image_url === imgUrl && (
                          <span className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1.5 py-0.5 font-medium">
                            {t('mainImage')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Published Toggle */}
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setNewsForm({ ...newsForm, is_published: !newsForm.is_published })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    newsForm.is_published ? 'bg-green-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      newsForm.is_published ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <Label className="text-sm font-medium">
                  {newsForm.is_published ? t('published') : t('draft')}
                </Label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-slate-200 flex justify-end space-x-3">
              <Button variant="outline" onClick={closeNewsForm} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleSaveNews} 
                disabled={savingNews}
                className="bg-green-600 hover:bg-green-700 text-white rounded-none"
              >
                {savingNews ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingArticle ? t('saveChanges') : t('createArticle')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
